import { safePercent } from "../../utils/format-number";
import StatNumber from "./stat-number";

type Props = { data: Record<string, any> | null };

const n = (v: unknown) => Number(v ?? 0) || 0;

/**
 * Payment plans: open, finished, failed.
 *
 * Open plans get a progress meter because "collected against total" is a genuine
 * ratio-to-a-limit. Finished and failed are terminal states with nothing left to
 * fill, so they get figures rather than a meter that would always read 100% or 0%.
 */
export default function PaymentPlansPanel({ data }: Props) {
  const d = data ?? {};

  const open = d.pendingPlans ?? {};
  const done = d.collectedPlans ?? {};
  const failed = d.failedPlans ?? {};

  const openCollected = n(open.collectedAmount);
  const openTotal = n(open.totalAmount);
  const openProgress = safePercent(openCollected, openTotal);

  return (
    <div className="flex flex-col gap-5">
      {/* Open plans — the one row with a real ratio to show. */}
      <div className="rounded-xl border border-edge-faint bg-overlay p-4">
        <div className="flex items-baseline justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
            <p className="text-[13px] font-medium text-text-secondary truncate">
              Open plans
            </p>
          </div>
          <p className="text-[18px] font-bold text-text-primary leading-none shrink-0">
            <StatNumber value={n(open.totalPlans)} unit="plans" />
          </p>
        </div>

        <div className="mt-3 h-1.5 w-full rounded-full bg-overlay-mid overflow-hidden">
          <div
            className="h-full rounded-full bg-indigo-500 transition-all duration-500"
            style={{ width: `${openProgress}%` }}
          />
        </div>

        <div className="mt-2.5 flex items-center justify-between gap-3">
          <span className="text-[12px] font-medium text-light-gray">
            Collected{" "}
            <span className="font-semibold text-text-primary">
              <StatNumber value={openCollected} currency />
            </span>
          </span>
          <span className="text-[12px] font-medium text-light-gray">
            Left{" "}
            <span className="font-semibold text-text-primary">
              <StatNumber value={n(open.leftTopay)} currency />
            </span>
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-edge-faint bg-overlay p-4">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <p className="text-[12px] font-medium text-light-gray truncate">
              Finished
            </p>
          </div>
          <p className="mt-1.5 text-[20px] font-bold text-text-primary leading-none">
            <StatNumber value={n(done.totalPlans)} unit="plans" />
          </p>
          <p className="mt-1.5 text-[12px] font-semibold text-text-secondary">
            <StatNumber value={n(done.collectedAmount)} currency />
          </p>
        </div>

        <div className="rounded-xl border border-edge-faint bg-overlay p-4">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
            <p className="text-[12px] font-medium text-light-gray truncate">Failed</p>
          </div>
          <p className="mt-1.5 text-[20px] font-bold text-text-primary leading-none">
            <StatNumber value={n(failed.totalPlans)} unit="plans" />
          </p>
          <p className="mt-1.5 text-[12px] font-semibold text-text-secondary">
            <StatNumber value={n(failed.failedAmount)} currency />
          </p>
        </div>
      </div>
    </div>
  );
}
